import {
    Button,
    Drawer,
    Input,
    Typography,
} from 'antd';
import * as React from 'react';
import Loader from '../common/Loader';
import { getLocationServiceabilityTat } from '../../network/common.api';
import { StylesProps } from '../../theme/jss-types';
import { serviceAvailabilityStyles } from './service-availability.styles';
import { useTranslation } from 'react-i18next';
import { NAVBAR_HEIGHT } from 'library/globals';
import CrossIcon from '../../assets/cross';
import {
    ExpandAltOutlined,
    CheckCircleFilled,
    EnvironmentFilled,
    InfoCircleFilled,
} from '@ant-design/icons';
import { getPincodeServiceabilityRoute } from 'routing/routing-helper';
import { bindActionCreators } from 'redux';
import GenericHoc from 'components/common/generic-hoc';
import { ReduxStore } from 'reducers/redux.types';
import { HocOptions } from 'components/common/generic-hoc.types';
import { storeExpandingState } from 'actions/miscellaneous-actions';
import ContentFilledIcon from 'assets/contents-filled';
import { OtherBuckets, Pincodes, Service } from 'library/Types';
import { Master } from 'types/master-data-types';
import FallbackPage from '../common/fallback';

const { Paragraph } = Typography;

interface PincodeServiceProps extends StylesProps<ReturnType<typeof serviceAvailabilityStyles>> {
    onClose: () => void;
    shouldRenderInDrawer?: boolean;
    expandState: any;
    setStateForExpandAction: (data: any) => void;
    history: any,
    master: Master,
    pincodes?: Pincodes,
}

const PincodeService = (props: PincodeServiceProps) => {
    const {
        classes,
        onClose,
        expandState,
        setStateForExpandAction,
        shouldRenderInDrawer,
        history,
        master,
        pincodes,
    } = props;
    const { t } = useTranslation();
    const [services, setService] = React.useState<Service[]>(
        expandState?.apiResponse?.services || [],
    );
    const [loading, setLoading] = React.useState<boolean>(false);
    const [toggle, setToggle] = React.useState<boolean>(true);
    const [servicesLoaded, setServicesLoaded] = React.useState<boolean>(false);

    const [pincodeData, setPincodeData] = React.useState<Record<string, any>>(
        expandState?.pincodeData
        || (pincodes?.srcPincode && pincodes?.dstPincode
            ? { src: { pincode: pincodes.srcPincode }, dst: { pincode: pincodes.dstPincode } }
            : { src: {}, dst: {} }),
    );

    const customerProperties = master?.properties;
    const enable_customer_config = master?.parts_to_show?.enable_customer_config || false;
    let filterServicesWithoutTat = false;
    if (customerProperties && enable_customer_config) {
        filterServicesWithoutTat = customerProperties?.filter_services_without_tat;
    }

    const handlePincodeChange = (e: any, type: string) => {
        const pincode = e?.target?.value?.replace(/[^a-zA-Z0-9]/g, '');

        setPincodeData({
            ...pincodeData,
            [type]: {
                ...pincodeData[type],
                pincode,
                // TODO: as of now this is not handled in serviceability,
                // In future this check will be removed.
                valid: pincode.length === 6,
                city: '',
                state: '',
                country: '',
            },
        });

        setServicesLoaded(false);
    };

    const loadServiceabilityTat = async () => {
        setLoading(true);
        const storage = window.localStorage;
        const response = await getLocationServiceabilityTat({
            srcAddress: {
                pincode: pincodeData?.src?.pincode,
            },
            dstAddress: {
                pincode: pincodeData?.dst?.pincode,
            },
            clientCode: storage.getItem('userCode'),
            filterServicesWithoutTat,
        });
        setServicesLoaded(true);
        setService(response?.data || []);
        setLoading(false);
    };

    React.useEffect(() => {
        if (
            pincodeData?.src?.pincode
            && pincodeData?.dst?.pincode
        ) {
            setPincodeData({
                src: {
                    ...pincodeData.src,
                    valid: pincodeData?.src?.pincode.length === 6,
                },
                dst: {
                    ...pincodeData.dst,
                    valid: pincodeData?.src?.pincode.length === 6,
                },
            });

            if (pincodeData?.src?.pincode.length === 6 && pincodeData?.src?.pincode.length === 6) {
                loadServiceabilityTat();
            }
        }
    }, [toggle]);

    const renderHeader = () => {
        return (
            <div className={classes.header}>
                <div className={classes.addText}>
                    {shouldRenderInDrawer && (
                        <CrossIcon onClick={() => onClose()} alt="close" className={classes.closeIcon} />
                    )}
                    <span>
                        {t('pincode_serviceability')}
                    </span>
                    {
                        shouldRenderInDrawer && (
                            <ExpandAltOutlined
                                className={classes.expandIcon}
                                onClick={() => {
                                    setStateForExpandAction({
                                        pincodeData,
                                        apiResponse: {
                                            services,
                                        },
                                        route: getPincodeServiceabilityRoute(),
                                        filterServicesWithoutTat,
                                    });
                                    onClose();
                                    history.push(getPincodeServiceabilityRoute());
                                }}
                            />
                        )
                    }
                </div>
            </div>
        );
    };

    const disableButton = () => {
        return !pincodeData?.dst?.valid || !pincodeData?.src?.valid;
    };


    const renderPincodeLabel = (type: string, title: string) => {
        return (
            <span className={classes.name}>
                {type === 'src' ? (
                    <span style={{
                        backgroundColor: '#F0ECFD',
                        width: 20,
                        height: 20,
                        marginRight: 5,
                        borderRadius: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                    >
                        <ContentFilledIcon style={{
                            color: '#6A3DF5',
                            backgroundColor: 'transparent',
                        }}
                        />
                    </span>
                ) : (
                    <span style={{
                        backgroundColor: '#EDF8F1',
                        width: 20,
                        height: 20,
                        marginRight: 5,
                        borderRadius: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                    >
                        <EnvironmentFilled style={{
                            color: '#5CAF7E',
                        }}
                        />
                    </span>
                )}
                {title}
                *
            </span>
        );
    };

    const renderValidation = (type: string) => {
        const isValid = pincodeData[type]?.valid;

        return (
            <div className={classes.pincodeValidation} style={{ visibility: isValid ? 'visible' : 'hidden' }}>
                <span style={{ marginRight: 5 }}>
                    {pincodeData[type]?.city}
                </span>
                <CheckCircleFilled />
            </div>
        );
    };

    const renderPincode = (type: string, title: string) => {
        return (
            <div className={classes.singleField}>
                {renderPincodeLabel(type, title)}
                <Input
                    style={{
                        boxSizing: 'border-box',
                        height: 32,
                        border: '1px solid #999999',
                        borderRadius: 4,
                    }}
                    className={classes.pincodeInput}
                    value={pincodeData[type]?.pincode}
                    placeholder={title}
                    suffix={renderValidation(type)}
                    onChange={(e) => handlePincodeChange(e, type)}
                />
            </div>
        );
    };


    const renderPincodeInputs = () => {
        return (
            <div className={classes.fields}>
                {renderPincode('src', t('origin_pincode'))}
                {renderPincode('dst', t('destination_pincode'))}
            </div>
        );
    };

    const renderSubmitButton = () => {
        const disable = disableButton();
        return (
            <Button
                className={classes.submitButton}
                type="primary"
                disabled={disable}
                onClick={() => setToggle(!toggle)}
            >
                {t('check')}
            </Button>
        );
    };

    // const renderLine = () => {
    //     return <div className={classes.hr} />;
    // };

    const renderServiceTat = (config: any) => {
        return (
            <div className={classes.serviceableTatPair}>
                <span className={classes.name}>{config?.name}</span>
                {config.value && (
                    <span className={classes.value}>
                        {`${config?.value} days`}
                    </span>
                )}
            </div>
        );
    };

    const renderServices = () => {
        if (loading) {
            return <Loader zIndex={5} />;
        }

        if (!servicesLoaded) {
            return null;
        }

        if (!services?.length) {
            return (
                <FallbackPage
                    errorMsg={t('pincode_serviceability_error')}
                />
            );
        }
        return (
            <div className={classes.services}>
                <div className={classes.availableText}>
                    Available Services
                </div>
                {services?.map((service) => {
                    return renderServiceTat({
                        name: `${service?.serviceType} (${service?.serviceTypeId})`,
                        value: service?.tat,
                    });
                })}
            </div>
        );
    };

    const renderHeaderPageView = () => {
        return (
            <>
                <div className={classes.heading}>
                    <h2 className={classes.headingTitle}>
                        {t('pincode_serviceability')}
                    </h2>
                </div>
            </>
        );
    };

    const renderSubHeading = () => {
        return (
            <div className={classes.subHeading}>
                Enter the pin code to check the serviceability of your shipping address.
            </div>
        );
    };

    const renderBetweenPincodes = () => {
        return (
            <Paragraph>
                <pre className={classes.singleInfoDesc}>
                    <div className={classes.singleInfoDescIcon}>
                        <InfoCircleFilled />
                    </div>
                    <div className={classes.singleInfoDescText}>
                        A set of origin and destination pincodes can be used to check the available services.
                    </div>
                </pre>
            </Paragraph>
        );
    };

    return shouldRenderInDrawer ? (
        <Drawer
            visible
            width="45%"
            title={renderHeader()}
            onClose={() => onClose()}
            className={classes.main}
            style={{ padding: shouldRenderInDrawer ? 0 : '12px 16px' }}
            closable={false}
        >
            {renderSubHeading()}
            <div>
                <div className={classes.pincodeBox}>
                    {renderPincodeInputs()}
                    {renderBetweenPincodes()}
                    {renderSubmitButton()}
                </div>
                { (pincodeData?.dst?.valid && pincodeData?.src?.valid)
                    ? (renderServices()) : null}
            </div>
        </Drawer>
    ) : (
        <div
            className={classes.main}
            style={{
                height: `calc(100vh - ${NAVBAR_HEIGHT}px - 15px)`,
                backgroundColor: '#ffffff',
            }}
        >
            {renderHeaderPageView()}
            {renderSubHeading()}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div className={classes.pincodeBox}>
                    {renderPincodeInputs()}
                    {renderBetweenPincodes()}
                    {renderSubmitButton()}
                </div>
                { (pincodeData?.dst?.valid && pincodeData?.src?.valid)
                    ? (renderServices()) : null}
            </div>
        </div>
    );
};

const mapStateToProps = (state: ReduxStore) => {
    const { master, miscellaneousState } = state;
    const { [OtherBuckets.EXPAND_STATE]: expandState } = miscellaneousState || {};
    return {
        expandState,
        master,
    };
};

const mapDispatchToProps = (dispatch: any) => {
    const actions = {
        setStateForExpandAction: storeExpandingState,
    };
    return bindActionCreators(actions, dispatch);
};

const hocConfig: HocOptions = {
    connectJss: {
        useJss: true,
        styleSheet: serviceAvailabilityStyles,
    },
    connectRedux: {
        useRedux: true,
        mapStateToProps,
        mapDispatchToProps,
    },
    connectRouter: true,
    connectTranslession: true,
};

export default GenericHoc(hocConfig)(PincodeService);
